import './Dashboard.less';
import React from 'react';
import { Alert, Row, Col, Card, Empty } from 'antd';
import { GenericProcess } from '../../process/types';
import useBatchUrlAnalysisProcessesPolling from '../process/hooks/useBatchUrlAnalysisProcessesPolling';
import { useFetchWebsiteAuditOverviewQuery } from '../auditReport/service';
import { skipToken } from '@reduxjs/toolkit/query';
import OverviewHeader from './OverviewHeader';
import PageGroupInsights from './PageGroupInsights';
import StrengthsAndWeaknesses from './StrengthsAndWeaknesses';
import LoaderCard from '../shared/LoaderCard';
import CardTitleWithProgressIndicator from '../shared/CardTitleWithProgressIndicator';

type Props = {
    websiteGuid: string;
};

function getAlertTitle(process: GenericProcess | null) {
    if (process && process.subprocesses_count >= 0) {
        const finished = process.completed_subprocesses_count + process.failed_subprocesses_count + process.timeout_subprocesses_count;
        const progress = Math.floor((finished / process.subprocesses_count) * 100);
        return `SEO audit in progress - ${progress}% (${finished}/${process.subprocesses_count})`;
    }
    return 'SEO audit in progress';
}

export default function DashboardOverview({ websiteGuid }: Props) {
    const { isRunningBatchAnalysis, activeBatchUrlAnalysisProcess, isLoadingRunningProcesses } = useBatchUrlAnalysisProcessesPolling(websiteGuid);
    const { data: websiteOverview, isLoading: isLoadingWebsiteOverview } = useFetchWebsiteAuditOverviewQuery(
        isRunningBatchAnalysis || isLoadingRunningProcesses ? skipToken : { w_guid: websiteGuid }
    );
    const isLoading = isLoadingRunningProcesses || isLoadingWebsiteOverview;

    return (
        <>
            {isLoading && <LoaderCard style={{ marginBottom: '24px' }} />}

            {!isLoading && isRunningBatchAnalysis && (
                <Alert
                    className="audit-in-progress-alert"
                    type="info"
                    showIcon
                    message={
                        <div className="analysis-progress">
                            <div className="title">{getAlertTitle(activeBatchUrlAnalysisProcess)}</div>
                            <div className="loader"></div>
                        </div>
                    }
                    description={
                        <>
                            <span className="alert-message">We are currently analyzing your monitored URLs for SEO issues. This may take several minutes or more, depending on the complexity of
                                your site's pages.&nbsp;
                            </span>
                            <span className="alert-message">
                                Feel free to leave this page - we'll email and notify you when it's complete.
                            </span>
                        </>
                    }
                />
            )}

            {!isLoading && !isRunningBatchAnalysis && websiteOverview && (
                <OverviewHeader
                    websiteGuid={websiteGuid}
                    faviconUrl={websiteOverview.favicon_url}
                    homepageScreenShotUrl={websiteOverview.homepage_screenshot_url}
                    summary={websiteOverview.summary}
                />
            )}
            {!isLoading && <Row className="audit-overview-row" gutter={24} type="flex" justify="space-between">
                <Col span={12}>
                    <Card
                        className="overview-card page-group-insights"
                        title={
                            <CardTitleWithProgressIndicator
                                title="Page Group Insights"
                                description="SEO audit in progress"
                                isInProgress={isRunningBatchAnalysis}
                            />
                        }
                    >
                        {isRunningBatchAnalysis && <Empty description="" />}
                        {!isRunningBatchAnalysis && websiteOverview && <PageGroupInsights
                            websiteGuid={websiteGuid}
                            contentOverview={websiteOverview.page_group_insights.content_overview}
                            keyPageGroups={websiteOverview.page_group_insights.key_page_groups.slice(0, 2)}
                        />}
                    </Card>
                </Col>

                <Col span={12}>
                    <Card
                        className="overview-card strengths-weaknesses"
                        title={
                            <CardTitleWithProgressIndicator
                                title="Strengths & Weaknesses"
                                description="SEO audit in progress"
                                isInProgress={isRunningBatchAnalysis}
                            />
                        }
                    >
                        {isRunningBatchAnalysis && <Empty description="" />}
                        {!isRunningBatchAnalysis && websiteOverview && <StrengthsAndWeaknesses
                            websiteGuid={websiteGuid}
                            strengths={websiteOverview.key_strengths}
                            weaknesses={websiteOverview.key_weaknesses}
                        />}
                    </Card>
                </Col>
            </Row>
            }
        </>
    );
}
